import { checkReportRange, type ChecklistComplianceRow, type ReportResult } from "@fts/core";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { checklistSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { formatNum, formatPct } from "../../lib/report-figures";
import { ChartPanel, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { checklistChart, checklistSummary, toSummaryLines } from "./presenters";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, parseFilters, rangeKey, siteLabel, useLatest, useLoad, settleWithin, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";

/** Kontrol listesi uyum raporu (ADR 0018): shifts used, checklists submitted, missed, compliance, faults, MTTR. */
export function ChecklistReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: null, ...range });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to];
  const state = useLoad<ReportResult<ChecklistComplianceRow>>(tenantId && !issue ? () => provider.reportChecklist(query(tenantId)) : null, deps);
  // The previous equal period through the same report call (same filters, same RLS): the comparison only.
  const prevRange = previousOf(f);
  // Fired only once the main result is in (never two report calls racing for the same screen).
  const prev = useLoad<ReportResult<ChecklistComplianceRow>>(tenantId && !issue && state.status === "ready" ? () => provider.reportChecklist(query(tenantId, prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const rows = state.data?.rows ?? [];
  const ctx = { t, locale, lang };
  const prevRows = prev.status === "ready" ? (prev.data?.rows ?? null) : null;
  const summary = checklistSummary(ctx, rows, prevRows);
  const chart = checklistChart(ctx, rows);
  const filters = filterSummary(t, data, f, { operator: false });
  const num = (v: number) => formatNum(v, locale, 1);
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "checklist",
        reportName: t("reports.checklist.title"),
        slug: t("reports.checklist.slug"),
        part: `${f.from}_${f.to}`,
        tz: state.data.tz,
        filters,
        period: periodText(t, lang, f.from, f.to),
        rows: rows.length,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportChecklist(query(tenantId), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return {
            sheets: [checklistSheet(t, r.rows)],
            tz: r.tz,
            summary: toSummaryLines(ctx, checklistSummary(ctx, r.rows, p?.rows ?? null)),
            chart: { title: t("reports.checklist.chart.title"), spec: checklistChart(ctx, r.rows), note: t("reports.checklist.chart.note") },
          };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PrintBrand title={t("reports.checklist.title")} tz={state.data?.tz ?? null} />
      <PageHeader title={t("reports.checklist.title")} subtitle={t("reports.checklist.subtitle")} eyebrow={t("nav.group.reports")} />
      <FilterBar data={data} path="/reports/checklist" filters={f} operator={false} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportContext chips={contextChips(t, filters)} from={f.from} to={f.to} tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && rows.length > 0 && <SummaryCards items={summary} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "ready" && rows.length > 0 && <ChartPanel title={t("reports.checklist.chart.title")} spec={chart} note={t("reports.checklist.chart.note")} />}
      <Panel flush meta={state.status === "ready" ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          rk ? <EmptyState compact icon="info" title={t(rk)} /> : <Loading />
        ) : rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/checklist" filters={f} siteName={siteLabel(t, data, f.siteId)} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col">{t("reports.col.shift")}</th>
                  <th scope="col" className="num">{t("reports.col.shiftsUsed")}</th>
                  <th scope="col" className="num">{t("reports.col.shiftsChecked")}</th>
                  <th scope="col" className="num">{t("reports.col.missed")}</th>
                  <th scope="col" className="num">{t("reports.col.compliance")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.faultsOpened")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.faultsResolved")}</th>
                  <th scope="col" className="num col-hide-md">{t("reports.col.mttr")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.vehicleId}|${r.shiftName ?? ""}`}>
                    <td className="mono">{r.fleetNo}</td>
                    <td>{r.hasShifts ? (r.shiftName ?? t("common.empty")) : <span className="muted">{t("reports.checklist.noShifts")}</span>}</td>
                    <td className="num">{r.shiftsUsed ?? t("common.empty")}</td>
                    <td className="num">{r.shiftsChecked ?? t("common.empty")}</td>
                    <td className="num">{r.missed ? <strong className="text-crit">{r.missed}</strong> : (r.missed ?? t("common.empty"))}</td>
                    <td className="num">{r.compliance === null ? t("common.empty") : formatPct(r.compliance, locale)}</td>
                    <td className="num col-hide-sm">{r.faultsOpened}</td>
                    <td className="num col-hide-sm">{r.faultsResolved}</td>
                    <td className="num col-hide-md">{r.mttrH === null ? t("common.empty") : t("reports.hours", { h: num(r.mttrH) })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.checklist.rules")}</p>
    </>
  );
}
